import {
  Dialog as MUIDialog,
  DialogTitle,
  DialogContent,
  Stack,
  Alert,
  Typography,
  Link,
} from '@mui/material';
import { useState } from 'react';

import ChevronLeft from '../../assets/icons/ChevronLeft';
import colors from '../../constants/colors';
import useAnalytics from '../../hooks/useAnalytics';
import Button, { TextButton } from '../styledComponents/Button';
import { RemoveButton } from '../styledComponents/IconButtons';
import CancelTAndCDialog from './Dialogs/CancelTAndCDialog';

interface TermsAndConditionsDialogProps {
  closeDialog: () => void;
  open: boolean;
  acceptTermsAndConditions: () => void;
  showAlert?: boolean;
  email: string;
  handleBackClick?: () => void;
  isLoading?: boolean;
  showPaperlessText?: boolean;
  showCancel?: boolean;
  showCancelTAndCDialog?: boolean;
}

const TermsAndConditionsDialog = ({
  closeDialog,
  open,
  acceptTermsAndConditions,
  showAlert = false,
  email,
  handleBackClick,
  isLoading = false,
  showPaperlessText = false,
  showCancel = false,
  showCancelTAndCDialog = true,
}: TermsAndConditionsDialogProps) => {
  const { track } = useAnalytics();
  const [showCancelDialog, setShowCancelDialog] = useState<boolean>(false);

  const handleCancelClick = () => {
    if (showCancelTAndCDialog) {
      setShowCancelDialog(true);
    } else {
      closeDialog();
    }
  };

  const handleAcceptClick = () => {
    acceptTermsAndConditions();
    track('Notification Conditions Accepted', {
      event_type: 'Link Accessed',
      location: 'Modal',
    });
    track('Preference Change Acknowledged', {
      event_type: 'Option Selected',
      location: 'Modal',
      preference_changed: 'Email',
      selection: 'Enroll',
    });
  };

  const closeCancelDialog = () => {
    closeDialog();
    setShowCancelDialog(false);
  };
  return (
    <>
      {!showCancelDialog && (
        <MUIDialog
          aria-labelledby="termsAndConditionsLabel"
          data-testid="termsAndConditionsDialog"
          open={open}
          onClose={() =>
            showCancelTAndCDialog ? setShowCancelDialog(true) : closeDialog()
          }
          sx={{ backdropFilter: 'blur(5px)', zIndex: 1500 }}
          PaperProps={{
            sx: {
              borderRadius: 4,
              margin: 0,
              px: { mobile: 3, tablet: 4 },
              position: { mobile: 'fixed', tablet: 'relative' },
              width: { mobile: 1, tablet: 480 },
              height: 'auto',
              maxHeight: 1,
            },
          }}
        >
          <Stack
            direction="row"
            alignItems="flex-end"
            justifyContent={handleBackClick ? 'space-between' : 'flex-end'}
            sx={{
              width: 1,
              my: 3,
            }}
          >
            {handleBackClick && (
              <TextButton
                sx={{ color: colors.csaaDark, fontWeight: 400, p: 0, ml: 0.5 }}
                onClick={handleBackClick}
              >
                <Stack sx={{ mr: 1.5 }}>
                  <ChevronLeft />
                </Stack>
                Back
              </TextButton>
            )}
            <RemoveButton
              aria-label="Close Terms and Conditions Modal"
              data-testid="closeIconUnenrollAutoPayDialogButton"
              onClick={() =>
                showCancelTAndCDialog
                  ? setShowCancelDialog(true)
                  : closeDialog()
              }
              autoFocus
            />
          </Stack>
          <Stack>
            <DialogTitle
              id="termsAndConditionsLabel"
              sx={{
                fontSize: 28,
                fontWeight: 700,
                fontFamily: 'RT Raleway',
                textAlign: 'center',
                color: colors.csaaBlack,
                lineHeight: '40px',
                p: 0,
                mb: { mobile: 2, tablet: 3 },
              }}
            >
              Terms and Conditions
            </DialogTitle>
            <DialogContent
              sx={{
                pt: 3,
                px: 0,
              }}
            >
              <Stack sx={{ pb: 3 }}>
                {showAlert && (
                  <Alert
                    severity="success"
                    sx={{
                      mb: { mobile: 2, tablet: 3 },
                      fontFamily: 'Cabin',
                      fontWeight: 400,
                      fontSize: { mobile: 16, tablet: 18 },
                      border: 2,
                      borderRadius: 2,
                      borderColor: 'success.light',
                    }}
                  >
                    Your policy is linked.
                  </Alert>
                )}
                <Stack>
                  <Typography
                    sx={{
                      fontSize: { mobile: 16, tablet: 18 },
                      fontWeight: 400,
                      fontFamily: 'Cabin',
                      mb: 3,
                      color: colors.csaaDark,
                    }}
                  >
                    By clicking &quot;Accept&quot;, you are accepting the{' '}
                    <Link
                      href="https://csaa-insurance.aaa.com/content/aaa-ie/b2c/en/misc/terms-of-use.html"
                      target="_blank"
                      onClick={() =>
                        track('Terms and Conditions Accessed', {
                          event_type: 'Link Accessed',
                          link: 'paperless preferences terms and conditions',
                          location: 'Modal',
                        })
                      }
                      underline="none"
                      sx={{
                        color: 'primary',
                        fontWeight: { mobile: 400, tablet: 700 },
                        fontSize: { mobile: 16, tablet: 18 },
                      }}
                    >
                      paperless preferences terms and conditions
                    </Link>{' '}
                    and choosing to receive your policy documents and/or bills
                    electronically with email notifications sent to {email}.
                    Policy documents include information about your policy,
                    privacy and other notices, and records. Bill notifications
                    include bills for insurance premiums and related fees. You
                    may opt out of receiving policy documents and bills
                    electronically at anytime, or update your email address by:
                  </Typography>
                  {showPaperlessText && (
                    <Typography
                      sx={{
                        fontFamily: 'Cabin',
                        fontSize: 16,
                        fontWeight: 700,
                        mb: 1,
                      }}
                    >
                      Editing your Paperless Settings
                    </Typography>
                  )}
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={0.5}
                    sx={{
                      fontFamily: 'Cabin',
                      fontSize: 16,
                      fontWeight: 700,
                      mb: 1,
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: 'Cabin',
                        fontSize: 16,
                        fontWeight: 700,
                      }}
                    >
                      Calling Insurance Service:{' '}
                    </Typography>
                    <Link
                      href="tel:+888.980.5650"
                      underline="none"
                      sx={{
                        fontSize: {
                          mobile: 16,
                          tablet: 16,
                        },
                        fontWeight: 700,
                        fontFamily: 'RT Raleway',
                        fontKerning: 'normal',
                        color: colors.csaaSpectrumBlue50,
                      }}
                    >
                      888.980.5650
                    </Link>
                  </Stack>
                  <Typography
                    sx={{
                      fontFamily: 'Cabin',
                      fontSize: 16,
                      fontWeight: 700,
                    }}
                  >
                    Visiting your local AAA branch
                  </Typography>
                </Stack>
              </Stack>
              <Stack spacing={{ mobile: 1, tablet: 2 }}>
                {showCancel && (
                  <Button
                    fullWidth
                    variant="outlined"
                    color="inherit"
                    data-testid="paymentMethodSelectCancel"
                    sx={{
                      borderWidth: 2,
                      fontWeight: 700,
                      textTransform: 'none',
                      fontSize: 18,
                      color: 'text.secondary',
                      px: 4,
                      py: 0.5,
                    }}
                    onClick={handleCancelClick}
                  >
                    Cancel
                  </Button>
                )}
                <Button
                  fullWidth
                  data-testid="termsAndConditionsDialogButton"
                  variant="contained"
                  sx={{
                    whiteSpace: 'nowrap',
                    height: { mobile: 36, tablet: 42 },
                  }}
                  onClick={handleAcceptClick}
                  loading={isLoading}
                >
                  Accept
                </Button>
              </Stack>
            </DialogContent>
          </Stack>
        </MUIDialog>
      )}
      {showCancelDialog && open && (
        <CancelTAndCDialog
          open={showCancelDialog}
          closeDialog={closeCancelDialog}
          handleAccept={acceptTermsAndConditions}
          isLoading={isLoading}
        />
      )}
    </>
  );
};

export default TermsAndConditionsDialog;
